import { PERMIT2_DOMAIN_NAME } from "@sablier/v2-constants";
import { framework } from "@sablier/v2-contracts";
import { BigNumber, _ } from "@sablier/v2-mixins";
import type { Outputs } from "@sablier/v2-contracts";
import type {
  IAddress,
  IPermit2,
  ISigner,
  IToken,
  IWagmiAddress,
} from "@sablier/v2-types";

const PERMIT_EXPIRATION = 30 * 24 * 60 * 60 * 1000;
const PERMIT_SIG_EXPIRATION = 30 * 60 * 1000;

interface IPermitAllowance {
  allowance: BigNumber;
  expiration: number;
  nonce: number;
}

interface ICreateSignature {
  amount: string;
  token: IToken | undefined;
  spender: IAddress | undefined;
  chainId: number | undefined;
  nonce: number;
  signer: ISigner;
  permit2A: IAddress;
}

/**
 * Convert a stringified IMilliseconds duration into a relative end date (relative to now)
 */
function toEndDate(time: number) {
  return new BigNumber(Date.now())
    .plus(new BigNumber(time))
    .dividedToIntegerBy(new BigNumber(1000))
    .toString();
}

export default class Permit {
  static async getAllowance(params: {
    owner: IAddress;
    spender: IAddress;
    token: IToken;
    chainId: number;
    permit2A: IAddress;
  }): Promise<IPermitAllowance | undefined> {
    const { owner, spender, token, chainId, permit2A } = params;
    const query = framework.contextualize(
      permit2A,
      chainId,
      "permit",
      "allowance",
      [
        owner as IWagmiAddress,
        token?.address as IWagmiAddress,
        spender as IWagmiAddress,
      ],
    );
    const previews = await framework.preview({ queries: [query] });
    const results = await framework.read(previews);

    const outputs = results[0].result as Outputs<"permit", "allowance">;

    return {
      allowance: new BigNumber(outputs[0].toString()),
      expiration: outputs[1],
      nonce: outputs[2],
    };
  }

  static async createSignature(params: ICreateSignature): Promise<IPermit2> {
    const { token, spender, amount, chainId, nonce, signer, permit2A } = params;

    _.expect(chainId, "amount");
    _.expect(chainId, "chainId");
    _.expect(token, "token");
    _.expect(spender, "spender");
    _.expect(nonce, "nonce");
    _.expect(signer, "signer");

    const expiration = toEndDate(PERMIT_EXPIRATION);
    const sigDeadline = toEndDate(PERMIT_SIG_EXPIRATION);

    const value = {
      details: {
        token: token!.address as IWagmiAddress,
        amount: _.toBigInt(amount),
        expiration: _.toNumber(expiration),
        nonce,
      },
      spender: spender as IWagmiAddress,
      sigDeadline: _.toBigInt(sigDeadline),
    } as const;

    const domain = {
      name: PERMIT2_DOMAIN_NAME,
      chainId,
      verifyingContract: permit2A as IWagmiAddress,
    } as const;

    const types = {
      PermitSingle: [
        { name: "details", type: "PermitDetails" },
        { name: "spender", type: "address" },
        { name: "sigDeadline", type: "uint256" },
      ],
      PermitDetails: [
        { name: "token", type: "address" },
        { name: "amount", type: "uint160" },
        { name: "expiration", type: "uint48" },
        { name: "nonce", type: "uint48" },
      ],
    } as const;

    const signature = await framework.sign({
      account: signer.account?.address,
      domain,
      types,
      primaryType: "PermitSingle",
      message: value,
    });

    /**
     * While the signature needs to be constructed with primitive types,
     * the transaction build will require strong-typed attributes
     */

    return {
      permitSingle: {
        details: {
          amount: _.toBigInt(value.details.amount),
          expiration: _.toNumber(value.details.expiration),
          nonce: _.toNumber(value.details.nonce),
          token: value.details.token as IWagmiAddress,
        },
        spender: value.spender as IWagmiAddress,
        sigDeadline: _.toBigInt(value.sigDeadline),
      },
      signature,
    };
  }

  static async createSignatureSafe(
    params: ICreateSignature,
  ): Promise<IPermit2> {
    const { token, spender, amount, chainId, nonce, signer, permit2A } = params;

    if (
      !_.expect(amount, "amount") ||
      !_.expect(chainId, "chainId") ||
      !_.expect(token, "token") ||
      !_.expect(spender, "spender") ||
      !_.expect(nonce, "nonce") ||
      !_.expect(signer, "signer")
    ) {
      throw new Error("Missing parameters");
    }

    const expiration = toEndDate(PERMIT_EXPIRATION);
    const sigDeadline = toEndDate(PERMIT_SIG_EXPIRATION);

    const value = {
      details: {
        token: token!.address as IWagmiAddress,
        amount,
        expiration: _.toNumber(expiration),
        nonce,
      },
      spender: spender as IWagmiAddress,
      sigDeadline,
    } as const;

    const domain = {
      name: PERMIT2_DOMAIN_NAME,
      chainId,
      verifyingContract: permit2A as IWagmiAddress,
    } as const;

    const types = {
      PermitSingle: [
        { name: "details", type: "PermitDetails" },
        { name: "spender", type: "address" },
        { name: "sigDeadline", type: "uint256" },
      ],
      PermitDetails: [
        { name: "token", type: "address" },
        { name: "amount", type: "uint160" },
        { name: "expiration", type: "uint48" },
        { name: "nonce", type: "uint48" },
      ],
    };

    const signature = await framework.safeSign({
      domain,
      types,
      message: value,
      primaryType: "PermitSingle",
    });

    /**
     * While the signature needs to be constructed with primitive types,
     * the transaction build will require strong-typed attributes
     */

    return {
      permitSingle: {
        details: {
          amount: _.toBigInt(value.details.amount),
          expiration: _.toNumber(value.details.expiration),
          nonce: _.toNumber(value.details.nonce),
          token: value.details.token as IWagmiAddress,
        },
        spender: value.spender as IWagmiAddress,
        sigDeadline: _.toBigInt(value.sigDeadline),
      },
      signature,
    };
  }
}
